import { makeBundle, makePrefixAdder } from '../utils/type-utils';
import requestBuilder from '../utils/request-builder';
import * as endpoints from '../../utils/api/endpoints';
import omit from 'lodash/omit';

const addPrefixTo = makePrefixAdder('TABLE_BUILDER_PRESETS');
const SWITCH_PRESET = addPrefixTo('SWITCH_PRESET');

const initPresetsBundle = makeBundle(addPrefixTo('INIT_PRESETS'));
const addPresetBundle = makeBundle(addPrefixTo('ADD_PRESET'));
const updatePresetBundle = makeBundle(addPrefixTo('UPDATE_PRESET'));
const deletePresetBundle = makeBundle(addPrefixTo('DELETE_PRESET'));

const mapItemToColumn = ({ id, label, value, groupName, createdAt }) => ({
    id,
    label,
    value,
    group: groupName,
    createdAt
});

const mapColumnToItem = ({ id, label, value, group }) => {
    const item = {
        label,
        value,
        groupName: group
    };

    if (id != null) {
        item.id = id;
    }

    return item;
};

const mapDtoToPreset = dto => ({
    id: dto.id,
    createdAt: dto.createdAt,
    columns: (dto.presets || []).map(mapItemToColumn)
});

const buildPresetPayload = (presetName, columns, id) => {
    const payload = {
        presetName,
        presets: columns.map(mapColumnToItem)
    };

    if (id != null) {
        payload.id = id;
    }

    return payload;
};

const mapPresetsFromResponse = data =>
    (Array.isArray(data) ? data : []).reduce((acc, dto) => {
        acc[dto.presetName] = mapDtoToPreset(dto);

        return acc;
    }, {});

export const buildInitPresets = pageName => () =>
    requestBuilder(initPresetsBundle)
        .withOptions({ pageName })
        .get(endpoints.configuratorPresets);

export const buildAddPreset = pageName => (presetName, columns) =>
    requestBuilder(addPresetBundle)
        .withOptions({ pageName, presetName })
        .post(endpoints.configuratorPresets, buildPresetPayload(presetName, columns));

export const buildUpdatePreset = pageName => (presetName, columns, id) =>
    requestBuilder(updatePresetBundle)
        .withOptions({ pageName, presetName, id })
        .put(endpoints.configuratorPreset(id), buildPresetPayload(presetName, columns, id));

export const buildDeletePreset = pageName => (presetName, id) =>
    requestBuilder(deletePresetBundle)
        .withOptions({ pageName, presetName, id })
        .del(endpoints.configuratorPreset(id));

const switchPreset = (pageName, presetName) => ({
    type: SWITCH_PRESET,
    payload: { pageName, presetName }
});

const defaultState = {};

// eslint-disable-next-line no-restricted-syntax -- TODO: legacy function declaration (baseline)
export function reducer(state = defaultState, action) {
    // eslint-disable-next-line no-restricted-syntax -- TODO: legacy forEach/switch (baseline)
    switch (action.type) {
        case initPresetsBundle.REQUEST:
        case addPresetBundle.REQUEST:
        case updatePresetBundle.REQUEST:
        case deletePresetBundle.REQUEST:
            return {
                ...state,
                [action.pageName]: {
                    ...state[action.pageName],
                    processing: true
                }
            };
        case initPresetsBundle.SUCCESS: {
            const presets = mapPresetsFromResponse(action.response.data);
            const activePreset = state[action.pageName]?.activePreset;

            return {
                ...state,
                [action.pageName]: {
                    activePreset: activePreset && presets[activePreset] ? activePreset : null,
                    processing: false,
                    presets
                }
            };
        }
        case addPresetBundle.SUCCESS:
        case updatePresetBundle.SUCCESS: {
            const dto = action.response.data;
            const preset = mapDtoToPreset(dto);

            return {
                ...state,
                [action.pageName]: {
                    ...state[action.pageName],
                    activePreset: dto.presetName,
                    processing: false,
                    presets: {
                        ...(state[action.pageName]?.presets || {}),
                        [dto.presetName]: preset
                    }
                }
            };
        }
        case deletePresetBundle.SUCCESS:
            return {
                ...state,
                [action.pageName]: {
                    ...state[action.pageName],
                    activePreset:
                        state[action.pageName]?.activePreset === action.presetName
                            ? null
                            : state[action.pageName]?.activePreset,
                    processing: false,
                    presets: omit(state[action.pageName]?.presets || {}, action.presetName)
                }
            };
        case initPresetsBundle.FAILURE:
        case addPresetBundle.FAILURE:
        case updatePresetBundle.FAILURE:
        case deletePresetBundle.FAILURE:
            return {
                ...state,
                [action.pageName]: {
                    ...state[action.pageName],
                    processing: false
                }
            };
        case SWITCH_PRESET: {
            const { pageName, presetName } = action.payload;
            const pageState = state[pageName];

            if (!pageState || pageState.activePreset === presetName) {
                return state;
            }

            return {
                ...state,
                [pageName]: {
                    ...pageState,
                    activePreset: presetName
                }
            };
        }
        default:
            return state;
    }
}

export const presetsSelector = (state, props) => state.tableBuilder[props.pageName]?.presets || {};
export const activePresetSelector = (state, props) => state.tableBuilder[props.pageName]?.activePreset || null;
export const presetsProcessingSelector = (state, props) => state.tableBuilder[props.pageName]?.processing || false;

export const makeInitPresets = ({ pageName }) => () => buildInitPresets(pageName)();
export const makeAddPreset = ({ pageName }) => (presetName, columns) => buildAddPreset(pageName)(presetName, columns);
export const makeUpdatePreset = ({ pageName }) => (presetName, columns, id) =>
    buildUpdatePreset(pageName)(presetName, columns, id);
export const makeDeletePreset = ({ pageName }) => (presetName, id) => buildDeletePreset(pageName)(presetName, id);
export const makeSwitchPreset = ({ pageName }) => presetName => switchPreset(pageName, presetName);
